import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useKey } from 'react-use';
import message from './utils/messages';
import { navigate } from './utils/constants';
import { HUMAN_TIMEZONES, ICAL_TIMEZONES } from '../../constants';
import TimezoneSelect from '../../components/TimeZoneDropDown/TimezoneSelect';

import { useDispatch, useSelector } from 'react-redux';
import { updateUserProfile } from '../../store/slices/authSlice';
import ScheduleHeader from '../../components/Schedule/ScheduleHeader';

const getTimezoneLabel = (value) => {
  const entry = Object.entries(HUMAN_TIMEZONES).find(
    ([key, val]) => val === value
  );
  return entry ? entry[0] : null;
};

const Toolbar = ({
  onNavigate,
  onViewChange,
  views,
  view,
  openNewEvent,
  messages: propMessages,
  label,
  detailOpenCreate,
  onCreate,
  generateEmbedCode,
  ownerDetail,
}) => {
  const { user } = useSelector(
    (state) => state.auth
  );
  const dispatch = useDispatch();
  const [timezone, setTimezone] = useState(user?.attributes?.stationTimezone);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [iframeWidth, setIframeWidth] = useState('100%');
  const [iframeHeight, setIframeHeight] = useState('600px');

  const messages = message(propMessages);



  // Handle keyboard shortcut
  useKey('n', (event) => {
    if (event.ctrlKey) {
      handleCreateNewEvent();
    }
  }, { event: 'keydown' });

  const handleNavigate = useCallback((action) => () => {
    onNavigate(action);
  }, [onNavigate]);

  const handleViewChange = useCallback((viewName) => () => {
    onViewChange(viewName);
  }, [onViewChange]);

  const handleCreateNewEvent = useCallback(() => {
    openNewEvent();
  }, [openNewEvent]);

  const viewNamesGroup = useCallback(() => {
    if (views.length > 1) {
      return views.map(name => (
        <button
          type="button"
          key={name}
          className={cn({ 'rbc-active': view === name })}
          onClick={handleViewChange(name)}
        >
          {messages[name]}
        </button>
      ));
    }
    return null;
  }, [views, view, messages, handleViewChange]);

  const extractTimezone = useCallback((rruleString) => {
    const tzidMatch = rruleString.match(/TZID=([^;:\n]*)/);
    return tzidMatch ? tzidMatch[1] : 'UTC';
  }, []);

  const mapTimezoneToICal = useCallback((tzid) => {
    return ICAL_TIMEZONES[tzid] || tzid;
  }, []);

  const updateRruleWithTimezone = useCallback((rruleString, tzid) => {
    if (rruleString.includes('TZID=')) {
      return rruleString.replace(/TZID=[^;:\n]*/, `TZID=${tzid}`);
    } else {
      return rruleString.replace('RRULE:', `RRULE;TZID=${tzid}:`);
    }
  }, []);

  const formatEventToICal = useCallback((event) => {
    const { rrule } = event;
    const extractedTzid = extractTimezone(rrule);
    const icalTzid = mapTimezoneToICal(extractedTzid);
    const updatedRrule = updateRruleWithTimezone(rrule, icalTzid);

    return (
      `BEGIN:VEVENT\n` +
      `SUMMARY:${event.title}\n` +
      `DESCRIPTION:${event.description || ''}\n` +
      `${updatedRrule}\n` +
      `UID:event-${event.id}\n` +
      `END:VEVENT\n`
    );
  }, [extractTimezone, mapTimezoneToICal, updateRruleWithTimezone]);


  const getEmbedCode = useCallback((owner) => {
    const API_baseurl = process.env.API_URL;
    const src = `${API_baseurl}/stats/widget.html?owner=${encodeURIComponent(owner)}`;
    return `<iframe src="${src}" width="${iframeWidth}" height="${iframeHeight}" style="border:none;"></iframe>`;
  }, [iframeWidth, iframeHeight]);

  const openEmbedDialog = useCallback(() => {
    setEmbedDialogOpen(true);
  }, []);

  const closeEmbedDialog = useCallback(() => {
    setEmbedDialogOpen(false);
  }, []);

  const copyEmbedCode = useCallback((owner) => {
    const text = getEmbedCode(owner);
    navigator.clipboard.writeText(text).then(() => {
      alert('Embed code copied!');
    });
  }, [getEmbedCode]);

  const handleEmbedInputChange = useCallback((field) => (event) => {
    const value = event.target.value;
    if (field === 'iframeWidth') {
      setIframeWidth(value);
    } else if (field === 'iframeHeight') {
      setIframeHeight(value);
    }
  }, []);

  // Simple saveAs implementation if file-saver is not available
  const saveAs = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  const handleTimezoneChange = (e) => {
    const newTimezone = HUMAN_TIMEZONES[e.target.value];
    setTimezone(newTimezone);

    dispatch(
      updateUserProfile({
        message: "Timezone Updated Successfully.",
        values: { stationTimezone: newTimezone }, // Assuming your backend expects 'tz' field
      })
    );
  };


  return (
    <div>
      <div className="rbc-toolbar">
        <span className="rbc-btn-group">
          <button type="button" onClick={handleNavigate(navigate.TODAY)}>
            {messages.today}
          </button>
          <button type="button" onClick={handleNavigate(navigate.PREVIOUS)}>
            {messages.previous}
          </button>
          <button type="button" onClick={handleNavigate(navigate.NEXT)}>
            {messages.next}
          </button>
        </span>

        <span className="rbc-toolbar-label">{label}</span>

        <span className="rbc-btn-group">{viewNamesGroup()}</span>
      </div>

      <div className="rbc-toolbar">
        <div className="col-md-4 ">
          <TimezoneSelect
            label="Timezone"
            id="timezone"
            name="tz"
            value={getTimezoneLabel(timezone)}
            onChange={handleTimezoneChange}
          />
        </div>
        <div className='col-md-8 d-flex justify-content-end'>
          <ScheduleHeader
            onCreate={onCreate}
            generateEmbedCode={generateEmbedCode}
          />
        </div>
      </div>

      {/* Embed Code Dialog */}
      {embedDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '4px',
            minWidth: '400px'
          }}>
            <h2>Embed Your Calendar</h2>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Iframe Width</label>
              <input
                type="text"
                value={iframeWidth}
                onChange={handleEmbedInputChange('iframeWidth')}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Iframe Height</label>
              <input
                type="text"
                value={iframeHeight}
                onChange={handleEmbedInputChange('iframeHeight')}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Embed Code</label>
              <textarea
                value={getEmbedCode(ownerDetail)}
                readOnly
                style={{
                  width: '100%',
                  padding: '8px',
                  height: '80px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginTop: '10px' }}>
              <button
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  marginRight: '10px',
                  cursor: 'pointer'
                }}
                onClick={() => copyEmbedCode(ownerDetail)}
              >
                Copy Code
              </button>
              <button
                onClick={closeEmbedDialog}
                style={{
                  backgroundColor: '#ccc',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '2px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Toolbar.propTypes = {
  onNavigate: PropTypes.func,
  onViewChange: PropTypes.func,
  views: PropTypes.array,
  view: PropTypes.any,
  openNewEvent: PropTypes.func,
  messages: PropTypes.object,
  label: PropTypes.string,
  detailOpenCreate: PropTypes.bool,
  ownerDetail: PropTypes.string,
};

export default Toolbar;